import { type QueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { Clips, type GetBoardStatsResponse, Workspaces } from '@air/api';
import { type Clip, ClipType } from '@air/api/types';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type GalleryItemType } from '~/components/Gallery/types';
import { defaultClipGetOptions, getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';
import { type TemporaryVideoCut } from '~/utils/canvases/getTemporaryVideoCut';

type AddSavedVideoCutsToAllViews = (params: {
  assets: Clip[];
  parentBoardId?: string;
  type: GalleryItemType.asset | GalleryItemType.file;
}) => Promise<unknown>;

type UpdateSavedVideoCutBoardStats = (
  boardId: string,
  updateFn: (existingData?: GetBoardStatsResponse) => GetBoardStatsResponse | undefined,
) => Promise<unknown>;

type SavedVideoCutGalleryType = GalleryItemType.asset | GalleryItemType.file;

type SavedVideoCutAsset = {
  assetId: string;
  versionId: string;
};

export type SaveTemporaryVideoCutsAsAssetsParams = {
  addAssetsToAllViews: AddSavedVideoCutsToAllViews;
  boardId: string | undefined;
  cuts: TemporaryVideoCut[];
  queryClient: Pick<QueryClient, 'ensureQueryData'>;
  updateBoardStats: UpdateSavedVideoCutBoardStats;
  workspaceId: string;
};

type ShowSavedVideoCutsOnBoardParams = {
  addAssetsToAllViews: AddSavedVideoCutsToAllViews;
  boardId: string | undefined;
  queryClient: Pick<QueryClient, 'ensureQueryData'>;
  savedCuts: SavedVideoCutAsset[];
  updateBoardStats: UpdateSavedVideoCutBoardStats;
};

const GALLERY_SHOW_FAILURE_CONTEXT = 'Failed to show a saved video cut on the board';

const countSavedCut = (totals: GetBoardStatsResponse, clip: Clip) => {
  totals.assetCount += clip.type === ClipType.nonMedia ? 0 : 1;
  totals.fileCount += clip.type === ClipType.nonMedia ? 1 : 0;
  totals.sizeBytes += clip.size;
};

/**
 * The asset is already saved. Failing this refresh would make a retry call saveResult again
 * and put a second copy on the board.
 */
const showSavedVideoCutsOnBoard = async ({
  addAssetsToAllViews,
  boardId,
  queryClient,
  savedCuts,
  updateBoardStats,
}: ShowSavedVideoCutsOnBoardParams) => {
  if (savedCuts.length === 0) {
    return;
  }

  const fetchedClips = await Promise.all(
    savedCuts.map(async (savedCut) => {
      const clipGetOptions = { ...defaultClipGetOptions, boardId };

      try {
        return await queryClient.ensureQueryData({
          queryKey: getClipWithoutWorkspaceIdKey({ clipId: savedCut.versionId, options: clipGetOptions }),
          queryFn: () => Clips.getWithNoWorkspaceId({ id: savedCut.versionId, options: clipGetOptions }),
        });
      } catch (error: unknown) {
        reportErrorToBugsnag({
          context: GALLERY_SHOW_FAILURE_CONTEXT,
          error,
          metadata: { boardId, versionId: savedCut.versionId },
        });

        return undefined;
      }
    }),
  );
  const clips = fetchedClips.flatMap((clip) => (clip ? [clip] : []));

  if (clips.length === 0) {
    return;
  }

  const clipsByGalleryType = new Map<SavedVideoCutGalleryType, Clip[]>();

  for (const clip of clips) {
    const galleryType = getAssetGalleryItemType(clip);
    const groupedClips = clipsByGalleryType.get(galleryType) ?? [];
    groupedClips.push(clip);
    clipsByGalleryType.set(galleryType, groupedClips);
  }

  try {
    await Promise.all(
      Array.from(clipsByGalleryType, ([type, assets]) =>
        addAssetsToAllViews({
          assets,
          parentBoardId: boardId,
          type,
        }),
      ),
    );

    if (boardId) {
      await updateBoardStats(boardId, (existingData) =>
        existingData
          ? produce(existingData, (draft) => {
              for (const clip of clips) {
                countSavedCut(draft, clip);
              }
            })
          : clips.reduce<GetBoardStatsResponse>(
              (totals, clip) => {
                countSavedCut(totals, clip);
                return totals;
              },
              { assetCount: 0, fileCount: 0, sizeBytes: 0 },
            ),
      );
    }
  } catch (error: unknown) {
    reportErrorToBugsnag({
      context: GALLERY_SHOW_FAILURE_CONTEXT,
      error,
      metadata: { boardId },
    });
  }
};

/** Saves each cut as a real asset (optionally into a board). Resolves false when any cut could not be saved. */
export const saveTemporaryVideoCutsAsAssets = async ({
  addAssetsToAllViews,
  boardId,
  cuts,
  queryClient,
  updateBoardStats,
  workspaceId,
}: SaveTemporaryVideoCutsAsAssetsParams): Promise<boolean> => {
  if (cuts.length === 0) {
    return true;
  }

  const savedCuts = await Promise.all(
    cuts.map(async ({ fileName, taskId }) => {
      try {
        return await callAirApi(() =>
          Workspaces.Tasks.AssetTransformer.saveResult({
            workspaceId,
            taskId,
            as: 'asset',
            options: { boardId, title: fileName },
          }),
        );
      } catch (error: unknown) {
        reportErrorToBugsnag({
          context: 'Failed to save a temporary video cut as an asset',
          error,
          metadata: { boardId, taskId },
        });

        return undefined;
      }
    }),
  );

  await showSavedVideoCutsOnBoard({
    addAssetsToAllViews,
    boardId,
    queryClient,
    savedCuts: savedCuts.flatMap((savedCut) => (savedCut ? [savedCut] : [])),
    updateBoardStats,
  });

  return savedCuts.every((savedCut) => savedCut !== undefined);
};
